import _uniqueId from "lodash/uniqueId";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Tab, Tabs, Text } from 'core/components';
import { observer } from 'mobx-react';
import { getDimensions } from '@kentik/ui-shared/query/query';
import { useDictionaryMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import $syn from 'app/stores/synthetics/$syn';
import styled from 'styled-components';
import { renderMetricValue } from 'app/views/boards/panels/ude/legendUtils';
import UdeEventsTable from 'app/views/boards/panels/ude/UdeEventsTable';
import UdeTable from './UdeTable';
import getRollupResults from './util/getRollupResults';

/** Dimensions that should render agent names instead of IDs */
const AGENT_DIMENSIONS = ['src_agent', 'dst_agent'];

/** Dimensions that should render test names instead of IDs */
const TEST_DIMENSIONS = ['test_id'];
/** Condensed legend item — color line indicator + label with visibility-based opacity. */
const CondensedItem = styled(Flex).withConfig({
  displayName: "UdeLegend__CondensedItem",
  componentId: "sc-4kzbkr-0"
})(["&&{cursor:", ";font-size:12px;white-space:nowrap;border-bottom:", ";opacity:", ";transition:opacity 0.15s ease;&:last-child{border-bottom:none;}&:hover{background:", ";border-radius:3px;}}"], ({
  $isCharted
}) => $isCharted ? 'pointer' : 'default', ({
  $isRight,
  theme
}) => $isRight ? `1px solid ${theme.borderColors.divider || 'rgba(128, 128, 128, 0.25)'}` : 'none', ({
  $isVisible,
  $isCharted
}) => {
  if (!$isCharted) {
    return 0.4;
  }
  return $isVisible ? 1 : 0.35;
}, ({
  theme,
  $isCharted
}) => $isCharted ? theme.backgrounds.tableHeaderHover : 'none');
const CondensedLegend = observer(({
  queryModel,
  position,
  dimensions,
  dimensionFormatter,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  rollups,
  showRollups
}) => {
  const {
    results
  } = queryModel;
  const isRightPos = position === 'right' || position === 'left';
  return /*#__PURE__*/React.createElement(Flex, {
    flexWrap: isRightPos ? 'nowrap' : 'wrap',
    flexDirection: isRightPos ? 'column' : 'row',
    gap: isRightPos ? '0px' : '4px 12px',
    p: 1
  }, results.rows.map((row, idx) => {
    const isVisible = queryModel.isSeriesVisible(row.id);
    const isCharted = queryModel.isRowCharted(row.id);
    const label = dimensions.map(dim => dimensionFormatter(dim.name, row.dimensions[dim.name])).join(' \u2013 ');
    return /*#__PURE__*/React.createElement(CondensedItem, {
      key: row.id,
      $isVisible: isVisible,
      $isCharted: isCharted,
      $isRight: isRightPos,
      alignItems: "center",
      gap: "4px",
      px: "6px",
      py: "4px",
      onClick: isCharted ? e => onRowClick(row.id, idx, e) : undefined,
      onMouseEnter: isCharted ? () => onRowMouseEnter(row.id) : undefined,
      onMouseLeave: isCharted ? onRowMouseLeave : undefined
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        color: queryModel.getSeriesColor(row.id) ?? '#999',
        fontSize: 16,
        lineHeight: 1,
        fontWeight: 'bold'
      }
    }, isVisible && isCharted ? '\u2501' : '\u2500'), /*#__PURE__*/React.createElement("span", {
      style: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 260,
        whiteSpace: 'nowrap'
      },
      title: label
    }, label), showRollups && rollups.length > 0 && /*#__PURE__*/React.createElement("span", {
      style: {
        opacity: 0.7,
        fontSize: 11,
        marginLeft: 'auto',
        paddingLeft: 8,
        whiteSpace: 'nowrap',
        textAlign: 'right'
      }
    }, rollups.map((rollup, rIdx) => /*#__PURE__*/React.createElement("span", {
      key: rollup.key,
      style: {
        marginLeft: rIdx > 0 ? 6 : 0
      }
    }, renderMetricValue(rollup.metric, rollup.values[idx], rollup.metricInfo)))));
  }));
});
function UdeLegend({
  queryModel,
  position = 'bottom',
  maxWidth: maxWidthOverride
}) {
  const tabsId = useRef(_uniqueId('ude-legend-tabs-'));
  const {
    query,
    results,
    settings
  } = queryModel;
  const isRight = position === 'right' || position === 'left';
  const isTableOnly = settings.chart.type === 'table';
  const mode = isTableOnly ? 'table' : queryModel.settings?.chart?.legend?.mode ?? 'condensed';
  const isCondensed = mode === 'condensed';
  const {
    measurement
  } = useDictionaryMeasurement(query.measurement, {
    lookback: query.time.lookback
  });
  const lastClickedIndexRef = useRef(null);
  const showRollups = isTableOnly || (queryModel.settings?.chart?.legend?.showRollups ?? true);
  const showEventsView = (measurement?.measurement?.startsWith('/events/') ?? false) && (queryModel.settings?.chart?.legend?.showEventsView ?? true);
  const [selectedTabId, setSelectedTabId] = useState('chart-legend');

  // Reset stale shift-click anchor when results change (rows may have shifted or shrunk)
  useEffect(() => {
    lastClickedIndexRef.current = null;
  }, [results]);

  /** Format a dimension value to a display string (resolves agent/test IDs to names). */
  const dimensionFormatter = useCallback((dimensionName, value) => {
    const strValue = String(value);
    if (AGENT_DIMENSIONS.includes(dimensionName) && strValue) {
      const agent = $syn.agents?.get(strValue);
      return agent?.label || strValue;
    }
    if (TEST_DIMENSIONS.includes(dimensionName) && strValue) {
      const test = $syn.tests?.get(strValue);
      return test?.get('display_name') || test?.get('name') || strValue;
    }
    return strValue;
  }, []);
  const rollups = useMemo(() => {
    if (!measurement) {
      return [];
    }
    return getRollupResults(queryModel, measurement);
  },
  // rollups depend on queryModel.query and queryModel.results
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [queryModel, queryModel.query, queryModel.results, queryModel.results.timestamps, measurement]);
  const handleRowClick = useCallback((rowId, rowIndex, event) => {
    // If the click originated from an <a> tag (e.g. SynthAgent/SynthTest links), let it navigate
    const target = event.target;
    if (target.closest('a')) {
      return;
    }
    const {
      rows
    } = results;
    if (event.shiftKey && lastClickedIndexRef.current !== null) {
      document.getSelection()?.empty(); // Clear text selection caused by shift-click
      // Shift+click: toggle range from last-clicked to current
      const start = Math.min(lastClickedIndexRef.current, rowIndex);
      const end = Math.max(lastClickedIndexRef.current, rowIndex);
      const targetVisible = !queryModel.isSeriesVisible(rowId);
      for (let i = start; i <= end; i++) {
        queryModel.setSeriesVisibility(rows[i].id, targetVisible);
      }
    } else if (event.metaKey || event.ctrlKey) {
      // Cmd+click (Mac) / Ctrl+click (Win): toggle single without affecting others
      queryModel.toggleSeriesVisibility(rowId);
    } else {
      // Plain click: "solo" behavior — if this is the only visible row, show all; otherwise solo it
      const visibleCount = rows.filter(r => queryModel.isSeriesVisible(r.id)).length;
      const isOnlyVisible = visibleCount === 1 && queryModel.isSeriesVisible(rowId);
      if (isOnlyVisible) {
        queryModel.showAllSeries();
      } else {
        rows.forEach(r => queryModel.setSeriesVisibility(r.id, r.id === rowId));
      }
    }
    lastClickedIndexRef.current = rowIndex;
  }, [queryModel, results]);
  const handleRowMouseEnter = useCallback(rowId => queryModel.setHoveredSeries(rowId), [queryModel]);
  const handleRowMouseLeave = useCallback(() => queryModel.setHoveredSeries(null), [queryModel]);
  function getOverflow() {
    if (showEventsView) {
      return 'hidden';
    }
    if (isCondensed) {
      return 'auto';
    }
    return undefined;
  }
  if (!measurement || settings.chart.type === 'timelineView') {
    return null;
  }
  const dimensions = getDimensions(query);
  const legendContent = /*#__PURE__*/React.createElement(Flex, {
    flex: 1
  }, isCondensed ? /*#__PURE__*/React.createElement(CondensedLegend, {
    queryModel: queryModel,
    position: position,
    dimensions: dimensions,
    dimensionFormatter: dimensionFormatter,
    onRowClick: handleRowClick,
    onRowMouseEnter: handleRowMouseEnter,
    onRowMouseLeave: handleRowMouseLeave,
    rollups: rollups,
    showRollups: showRollups
  }) : /*#__PURE__*/React.createElement(UdeTable, {
    queryModel: queryModel,
    measurement: measurement,
    rollups: rollups,
    onRowClick: handleRowClick,
    onRowMouseEnter: handleRowMouseEnter,
    onRowMouseLeave: handleRowMouseLeave
  }));
  const eventsTabsContent = /*#__PURE__*/React.createElement(Tabs, {
    flexed: true,
    bordered: true,
    overflowed: true,
    outerFlexStyles: {
      margin: '-1px',
      position: 'relative',
      minHeight: 0
    },
    id: tabsId.current,
    selectedTabId: selectedTabId,
    onChange: newTabId => setSelectedTabId(newTabId)
  }, /*#__PURE__*/React.createElement(Tab, {
    id: "chart-legend",
    title: /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center",
      px: 2,
      py: 1
    }, /*#__PURE__*/React.createElement(Text, {
      fontWeight: "bold"
    }, "Legend")),
    panel: /*#__PURE__*/React.createElement(Flex, {
      height: "100%",
      flex: 1,
      p: 0
    }, legendContent)
  }), /*#__PURE__*/React.createElement(Tab, {
    id: "events-view",
    title: /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center",
      px: 2,
      py: 1
    }, /*#__PURE__*/React.createElement(Text, {
      fontWeight: "bold"
    }, "Events")),
    panel: /*#__PURE__*/React.createElement(Flex, {
      flexDirection: "column",
      flex: 1,
      height: "100%"
    }, /*#__PURE__*/React.createElement(UdeEventsTable, {
      queryModel: queryModel
    }))
  }));
  return /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    className: "bp4-elevation-0",
    bg: "cardBackground",
    flex: isCondensed ? '0 1 auto' : 1,
    width: isRight ? 'fit-content' : undefined,
    maxHeight: isRight || isTableOnly ? '100%' : `min(40%, ${30 * (results.rows.length + 1)}px)`,
    maxWidth: isRight ? maxWidthOverride ?? '40%' : '100%',
    minWidth: isRight ? '200px' : undefined,
    overflow: getOverflow()
  }, showEventsView ? eventsTabsContent : legendContent);
}
export default observer(UdeLegend);